import React, { useState, useEffect } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { BrandMark } from '../components/BrandMark';
import { usePortalAuth } from './context/PortalAuthContext';
import { usePortalDashboard } from './hooks/usePortalDashboard';

const navItems = [
  { to: '/portal/dashboard', label: 'Dashboard' },
  { to: '/portal/invoices', label: 'Invoices' },
  { to: '/portal/support', label: 'Support' },
  { to: '/portal/services', label: 'Services' },
  { to: '/portal/tasks', label: 'Tasks' },
  { to: '/portal/packages', label: 'Packages' },
  { to: '/portal/profile', label: 'Profile' },
];

const PortalLayout: React.FC = () => {
  const { client, logout } = usePortalAuth();
  const { overdueInvoicesCount, openTicketsCount } = usePortalDashboard();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  const closeMenu = () => setMenuOpen(false);

  return (
    <div className="min-h-screen flex flex-col app-mesh-bg">
      <header className="sticky top-0 z-20 bg-slate-950/95 backdrop-blur-md border-b border-slate-800 shadow-soft">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14">
            <div className="flex items-center gap-4 lg:gap-8">
              <div className="flex items-center gap-2.5">
                <BrandMark size="sm" inverted />
                <span className="font-display font-semibold text-white hidden sm:inline tracking-tight">
                  Client Portal
                </span>
              </div>
              <nav className="hidden lg:flex gap-1">
                {navItems.map(({ to, label }) => {
                  const overdueBadge = to === '/portal/invoices' && overdueInvoicesCount > 0;
                  const supportBadge = to === '/portal/support' && openTicketsCount > 0;
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      end={to === '/portal/dashboard'}
                      className={({ isActive }) =>
                        `relative px-3.5 py-2 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
                          isActive
                            ? 'bg-teal-600 text-white'
                            : 'text-slate-300 hover:bg-white/10 hover:text-white'
                        }`
                      }
                    >
                      {label}
                      {overdueBadge && (
                        <span
                          className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-rose-400 ring-2 ring-slate-950"
                          title={`${overdueInvoicesCount} overdue`}
                          aria-hidden
                        />
                      )}
                      {supportBadge && (
                        <span
                          className="min-w-[1.25rem] h-5 px-1 rounded-full bg-white/20 text-xs font-semibold flex items-center justify-center"
                          title={`${openTicketsCount} open`}
                        >
                          {openTicketsCount > 99 ? '99+' : openTicketsCount}
                        </span>
                      )}
                    </NavLink>
                  );
                })}
              </nav>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <span
                className="text-sm text-slate-400 max-w-[120px] sm:max-w-[160px] truncate hidden sm:inline"
                title={client?.clientEmail || client?.clientUserName || ''}
              >
                {client?.clientEmail || client?.clientUserName || 'Client'}
              </span>
              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10 transition-colors"
                aria-label="Open menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => logout()}
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/5 px-3 sm:px-4 py-1.5 text-sm font-medium text-white hover:bg-white/10 transition-colors"
              >
                <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6A2.25 2.25 0 005.25 5.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l3-3m0 0l3 3m-3-3v12"
                  />
                </svg>
                <span className="hidden sm:inline">Log out</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
          aria-hidden
          onClick={closeMenu}
        />
      )}

      <div
        className={`fixed top-0 right-0 z-40 h-full w-full max-w-[280px] bg-white shadow-lift transform transition-transform duration-200 ease-out lg:hidden ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-label="Navigation menu"
      >
        <div className="flex flex-col h-full pt-14 pb-6">
          <div className="flex justify-end px-4 absolute top-3 right-2">
            <button
              type="button"
              onClick={closeMenu}
              className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
              aria-label="Close menu"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <nav className="flex flex-col gap-1 px-4 overflow-y-auto">
            {navItems.map(({ to, label }) => {
              const overdueBadge = to === '/portal/invoices' && overdueInvoicesCount > 0;
              const supportBadge = to === '/portal/support' && openTicketsCount > 0;
              return (
                <NavLink
                  key={to}
                  to={to}
                  end={to === '/portal/dashboard'}
                  onClick={closeMenu}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-sm font-medium transition-colors flex items-center justify-between gap-2 ${
                      isActive
                        ? 'bg-teal-50 text-teal-800'
                        : 'text-slate-700 hover:bg-slate-100'
                    }`
                  }
                >
                  {label}
                  <span className="flex items-center gap-2">
                    {overdueBadge && (
                      <span
                        className="h-2.5 w-2.5 rounded-full bg-rose-500"
                        title={`${overdueInvoicesCount} overdue`}
                        aria-hidden
                      />
                    )}
                    {supportBadge && (
                      <span className="min-w-[1.25rem] h-5 px-1.5 rounded-full bg-teal-100 text-teal-800 text-xs font-semibold flex items-center justify-center">
                        {openTicketsCount > 99 ? '99+' : openTicketsCount}
                      </span>
                    )}
                  </span>
                </NavLink>
              );
            })}
          </nav>
          <div className="mt-auto px-4 pt-4 border-t border-slate-200">
            <p
              className="text-xs text-slate-500 truncate"
              title={client?.clientEmail || client?.clientUserName || ''}
            >
              {client?.clientEmail || client?.clientUserName || 'Client'}
            </p>
          </div>
        </div>
      </div>

      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
};

export default PortalLayout;
